import { ChevronDown, Info, Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LearnMore } from "@/components/resources/LearnMore";
import { Link, useLocation } from "react-router-dom";

import { AmountInput } from "@/components/ui/amount-input";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatDateTime } from "@/lib/intl";

/*
 * The bid box: next valid amount pre-filled, one-tap bid, optional maximum (proxy) bid.
 * Optimistic press feedback; the server confirmation resolves the real state.
 */
export const BidBox = ({
  lot,
  minNext,
  me,
  locale,
  isAuthed,
  busy,
  auctionStatus,
  incrementRules,
  buyerCosts,
  termsHref,
  onBid,
  onOpenRegister,
}) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [showMax, setShowMax] = useState(false);
  const [maxAmount, setMaxAmount] = useState("");
  const [showMaxInfo, setShowMaxInfo] = useState(false); // tap-friendly tooltip
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [confirming, setConfirming] = useState(null); // cents awaiting explicit confirmation
  const maxInfoRef = useRef(null);
  const location = useLocation();
  const currency = lot.currency || "EUR";

  // Stepper +/−: el incremento vigente viene de la tabla ÚNICA del engine
  // (increment_rules del payload /lots/{id}/live) y lo determina la PUJA
  // ACTUAL — cambia de tramo automáticamente cuando el precio lo cruza.
  const stepCents = useMemo(() => {
    if (!incrementRules?.length) return null;
    const base = lot.bid_count > 0 ? lot.current_price_cents : lot.starting_price_cents || 0;
    let step = incrementRules[0].increment_cents;
    for (const r of incrementRules) if (base >= r.from_cents) step = r.increment_cents;
    return step;
  }, [incrementRules, lot.bid_count, lot.current_price_cents, lot.starting_price_cents]);

  const amountCents = Math.round(parseFloat(amount || "0") * 100) || 0;

  // Transparencia de precio del COMPRADOR: total estimado = remate + prima de
  // comprador (solo si el organizador la configuró) + IVA del organizador sobre
  // la base remate + prima (misma regla que events/commissions.buyer_vat_cents
  // y la factura de venta — fuente única). IVA 0 => sin línea de IVA.
  // JAMÁS incluye comisión AOS ni comisión del vendedor.
  const estimate = useMemo(() => {
    if (!amountCents) return null;
    const premium = buyerCosts?.premium_bps ? Math.floor((amountCents * buyerCosts.premium_bps) / 10000) : 0;
    const vat = buyerCosts?.vat_bps ? Math.floor(((amountCents + premium) * buyerCosts.vat_bps) / 10000) : 0;
    return { premium, vat, total: amountCents + premium + vat };
  }, [amountCents, buyerCosts]);

  const nudge = (dir) => {
    if (!stepCents) return;
    const base = amountCents || minNext || 0;
    const next = Math.max(minNext || 0, base + dir * stepCents);
    setAmount(String(next / 100));
    setConfirming(null);
  };

  // close the tooltip on outside tap / Esc (touch-friendly)
  useEffect(() => {
    if (!showMaxInfo) return undefined;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (e.type === "pointerdown" && maxInfoRef.current?.contains(e.target)) return;
      setShowMaxInfo(false);
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", close);
    };
  }, [showMaxInfo]);

  useEffect(() => {
    setAmount(minNext ? String(Math.round(minNext / 100)) : "");
    setConfirming(null);
  }, [minNext]);

  // confirmation expires quietly — never leave a stale "Confirm" armed
  useEffect(() => {
    if (confirming == null) return undefined;
    const id = setTimeout(() => setConfirming(null), 6000);
    return () => clearTimeout(id);
  }, [confirming]);

  const closed = ["sold", "passed", "withdrawn"].includes(lot.status);
  if (closed) return null;

  if (auctionStatus === "paused") {
    return (
      <div className="rounded-2xl bg-black/5 p-5 text-sm text-[#0A0A0A]" data-testid="bidbox-paused">
        {t("bid.pausedDesc")}
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="flex flex-col gap-3">
        <Link
          to={`/signin?next=${encodeURIComponent(location.pathname)}`}
          data-testid="bidbox-signin"
          className="flex items-center justify-center rounded-full bg-[#0A0A0A] text-white text-[17px] px-[22px] py-[12px] hover:bg-[#2b2b2b] transition-colors duration-300 w-full"
        >
          {t("bid.signInToBid")}
        </Link>
        <LearnMore slug="how-to-bid" section="placing-your-first-bid" className="self-center" />
      </div>
    );
  }

  if (!me || me.registration_status !== "approved") {
    if (me?.registration_status === "pending") {
      return (
        <div className="rounded-2xl bg-black/5 p-5 text-sm text-[#0A0A0A]" data-testid="bidbox-pending">
          {t("bid.registrationPending")}
        </div>
      );
    }
    if (me?.registration_status === "declined") {
      return (
        <div className="rounded-2xl bg-black/5 p-5 text-sm text-[#0A0A0A]" data-testid="bidbox-declined">
          {t("bid.registrationDeclined")}
        </div>
      );
    }
    return (
      <Button
        onClick={onOpenRegister}
        data-testid="bidbox-register"
        className="w-full rounded-full bg-[#0A0A0A] text-white text-[17px] h-auto px-[22px] py-[12px] hover:bg-[#2b2b2b]"
      >
        {t("bid.registerToBid")}
      </Button>
    );
  }

  // per-lot opening: scheduled lot — approved bidders see when it opens
  if (lot.status === "upcoming" && lot.starts_at && new Date(lot.starts_at) > new Date()) {
    return (
      <div className="rounded-2xl bg-black/5 p-5 text-sm text-[#0A0A0A]" data-testid="bidbox-scheduled">
        {t("bid.lotOpensAt", { date: formatDateTime(lot.starts_at, locale) })}
      </div>
    );
  }

  const submit = (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (cents <= 0) return;
    if (confirming !== cents) {
      setConfirming(cents); // first tap arms the confirmation
      return;
    }
    setConfirming(null);
    onBid({ amount_cents: cents });
  };

  const submitMax = (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(maxAmount || "0") * 100);
    if (cents > 0) onBid({ max_cents: cents }, () => setMaxAmount(""));
  };

  return (
    <div className="flex flex-col gap-4" data-testid="bidbox">
      <form onSubmit={submit} className="flex flex-col gap-2">
        <div className="flex items-stretch gap-2">
          {stepCents != null && (
            <Button
              type="button"
              variant="outline"
              onClick={() => nudge(-1)}
              disabled={busy || amountCents - stepCents < (minNext || 0)}
              data-testid="bid-decrement-button"
              aria-label={t("bid.decreaseBid", { amount: formatCurrency(stepCents, currency, locale) })}
              className="h-12 rounded-full px-3.5 sm:px-4 border-black/15 bg-white text-sm font-semibold tabular-nums text-[#0A0A0A] hover:bg-black/5 transition-colors duration-200 disabled:opacity-35 shrink-0 inline-flex items-center gap-1"
            >
              <Minus className="w-3.5 h-3.5" aria-hidden="true" />
              {formatCurrency(stepCents, currency, locale)}
            </Button>
          )}
          <div className="relative flex-1 min-w-0">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-[#737373]">€</span>
            <AmountInput
              value={amount}
              onValueChange={(v) => {
                setAmount(v);
                setConfirming(null);
              }}
              locale={locale}
              data-testid="bid-amount-input"
              aria-label={t("bid.customAmount")}
              className="pl-8 h-12 rounded-full text-base text-center font-semibold tabular-nums bg-white"
            />
          </div>
          {stepCents != null && (
            <Button
              type="button"
              variant="outline"
              onClick={() => nudge(1)}
              disabled={busy}
              data-testid="bid-increment-button"
              aria-label={t("bid.increaseBid", { amount: formatCurrency(stepCents, currency, locale) })}
              className="h-12 rounded-full px-3.5 sm:px-4 border-black/15 bg-white text-sm font-semibold tabular-nums text-[#0A0A0A] hover:bg-black/5 transition-colors duration-200 disabled:opacity-35 shrink-0 inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" aria-hidden="true" />
              {formatCurrency(stepCents, currency, locale)}
            </Button>
          )}
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="bid-submit-button"
          className={`h-12 w-full rounded-full text-white text-base active:scale-[0.97] transition-all duration-200 ${
            confirming != null ? "bg-[#1D4D3B] hover:bg-[#173e30]" : "bg-[#0A0A0A] hover:bg-[#2b2b2b]"
          }`}
        >
          {busy
            ? "…"
            : confirming != null
              ? estimate && estimate.total !== confirming
                ? `${t("bid.confirmBid", { amount: formatCurrency(confirming, currency, locale) })} · ${t("bid.confirmTotal", { amount: formatCurrency(estimate.total, currency, locale) })}`
                : t("bid.confirmBid", { amount: formatCurrency(confirming, currency, locale) })
              : t("bid.placeBid")}
        </Button>
        {confirming != null && (
          <p className="text-[11px] leading-relaxed text-[#737373]" data-testid="bid-binding-notice">
            {t("bid.bindingPre")}
            <a
              href={termsHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bid-binding-terms-link"
              className="font-medium text-[#1D4D3B] underline underline-offset-2"
            >
              {t("bid.bindingTerms")}
            </a>
            .
          </p>
        )}
      </form>
      <p className="text-xs text-[#737373] -mt-1" data-testid="bid-min-hint">
        {t("bid.minError", { amount: formatCurrency(minNext, currency, locale) })}
      </p>
      {estimate && (
        <div className="flex flex-col gap-1.5 -mt-2" data-testid="bid-estimate">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-medium text-[#0A0A0A]" data-testid="bid-estimate-total">
              {t("bid.estTotal", { amount: formatCurrency(estimate.total, currency, locale) })}
            </span>
            <button
              type="button"
              onClick={() => setShowBreakdown((v) => !v)}
              aria-expanded={showBreakdown}
              data-testid="bid-breakdown-toggle"
              className="inline-flex items-center gap-1 text-xs font-medium text-[#1D4D3B]"
            >
              {t(showBreakdown ? "bid.hideBreakdown" : "bid.viewBreakdown")}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${showBreakdown ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
          </div>
          {showBreakdown && (
            <dl className="rounded-xl bg-white border border-black/5 px-3.5 py-2.5 text-xs flex flex-col gap-1.5" data-testid="bid-breakdown">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-[#737373]">{t("bid.bdHammer")}</dt>
                <dd className="tabular-nums text-[#0A0A0A]" data-testid="bid-bd-hammer">{formatCurrency(amountCents, currency, locale)}</dd>
              </div>
              {estimate.premium > 0 && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-[#737373]">{t("bid.bdPremium", { pct: buyerCosts.premium_bps / 100 })}</dt>
                  <dd className="tabular-nums text-[#0A0A0A]" data-testid="bid-bd-premium">{formatCurrency(estimate.premium, currency, locale)}</dd>
                </div>
              )}
              {estimate.vat > 0 && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-[#737373]">{t("bid.bdVat", { pct: buyerCosts.vat_bps / 100 })}</dt>
                  <dd className="tabular-nums text-[#0A0A0A]" data-testid="bid-bd-vat">{formatCurrency(estimate.vat, currency, locale)}</dd>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 border-t border-black/5 pt-1.5 font-semibold">
                <dt className="text-[#0A0A0A]">{t("bid.bdTotal")}</dt>
                <dd className="tabular-nums text-[#0A0A0A]" data-testid="bid-bd-total">{formatCurrency(estimate.total, currency, locale)}</dd>
              </div>
            </dl>
          )}
        </div>
      )}
      <LearnMore slug="how-to-bid" section="placing-your-first-bid" className="-mt-2 w-fit" />

      <div className="flex items-center gap-1.5 relative" ref={maxInfoRef}>
        <button
          type="button"
          onClick={() => setShowMax((v) => !v)}
          data-testid="bid-max-toggle"
          className="flex items-center gap-1.5 text-sm font-medium text-[#1D4D3B] w-fit"
          aria-expanded={showMax}
        >
          {t("bid.maxBidTitle")}
          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showMax ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => setShowMaxInfo((v) => !v)}
          data-testid="bid-max-info"
          aria-label={t("bid.maxBidTooltipLabel")}
          aria-expanded={showMaxInfo}
          className="w-6 h-6 rounded-full flex items-center justify-center text-[#737373] hover:text-[#1D4D3B] transition-colors duration-200"
        >
          <Info className="w-4 h-4" aria-hidden="true" />
        </button>
        {showMaxInfo && (
          <div
            role="tooltip"
            data-testid="bid-max-tooltip"
            className="absolute left-0 bottom-full mb-2 z-30 w-72 rounded-xl bg-[#0A0A0A] text-white text-xs leading-relaxed px-4 py-3 shadow-xl"
          >
            {t("bid.maxBidTooltip")}
          </div>
        )}
      </div>
      {showMax && (
        <form onSubmit={submitMax} className="flex flex-col gap-2" data-testid="bid-max-form">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-[#737373]">{t("bid.maxBidDesc")}</p>
            <LearnMore slug="automatic-bidding" section="how-it-works" />
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-[#737373]">€</span>
              <AmountInput
                value={maxAmount}
                onValueChange={setMaxAmount}
                locale={locale}
                data-testid="bid-max-input"
                aria-label={t("bid.maxBidTitle")}
                className="pl-8 h-11 rounded-full text-sm bg-white"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              variant="outline"
              data-testid="bid-max-submit"
              className="h-11 rounded-full border-[#0A0A0A]/30 hover:border-[#0A0A0A] px-5"
            >
              {me.my_max_cents ? t("bid.updateMax") : t("bid.setMax")}
            </Button>
          </div>
          {me.my_max_cents && (
            <p className="text-xs text-[#1D4D3B]" data-testid="bid-my-max">
              {t("acct.myMax", { amount: formatCurrency(me.my_max_cents, currency, locale) })}
            </p>
          )}
        </form>
      )}
    </div>
  );
};
